import {
  DRAWER_FILTER_CONFIG,
  FILTERS_CONFIG,
  MODAL_FILTER_CONFIG,
  SHEET_FILTER_CONFIG,
  TAB_FILTER_CONFIG,
  type TApiFilterKey,
  type TConfiguredFilterGroup,
  type TFilters,
  type TRuntimeFilterConfig,
  type TStoreFilterKey,
} from "./filters.config";
import {
  DRAWER_FILTER_KEY,
  getDrawerDataFilterKey,
  getDrawerDataKey,
  getModalDataFilterKey,
  getModalDataKey,
  getSheetDataFilterKey,
  getSheetDataKey,
  isDrawerDataFilterKey,
  isModalDataFilterKey,
  isSheetDataFilterKey,
  isTabFilterKey,
  SHEET_FILTER_KEY,
  MODAL_FILTER_KEY,
  type TDrawerDataFilterKey,
  type TModalDataFilterKey,
  type TSheetDataFilterKey,
  type TTabFilters,
} from "./filters.schema";

type TSearchParamValue = string | string[] | undefined;

export type TFilterSearchParams =
  | Pick<URLSearchParams, "getAll" | "keys">
  | Record<string, TSearchParamValue>;

type TApiFilters<TFilterGroup extends keyof TFilters> = {
  [TFilter in keyof TFilters[TFilterGroup] as TFilter extends `${string}:${infer TKey}`
    ? TKey
    : never]: TFilters[TFilterGroup][TFilter];
};

const getSearchParamValues = (
  searchParams: TFilterSearchParams,
  filter: string,
): string[] => {
  if ("getAll" in searchParams && typeof searchParams.getAll === "function") {
    return searchParams.getAll(filter);
  }

  const value = (
    searchParams as Record<string, TSearchParamValue>
  )[filter];

  if (value === undefined) {
    return [];
  }

  if (Array.isArray(value)) {
    return value;
  }

  return [ value ];
};

const getFilterConfigEntries = (
  filterGroup: TConfiguredFilterGroup,
): Array<[ string, TRuntimeFilterConfig ]> => Object.entries(
  FILTERS_CONFIG[filterGroup],
) as unknown as Array<[ string, TRuntimeFilterConfig ]>;

const getSearchParamKeys = (
  searchParams: TFilterSearchParams,
): string[] => {
  if ("keys" in searchParams && typeof searchParams.keys === "function") {
    return [ ...searchParams.keys() ];
  }

  return Object.keys(searchParams);
};

const isDefaultValue = (value: unknown, defaultValue: unknown): boolean => {
  if (Array.isArray(value) && Array.isArray(defaultValue)) {
    return value.length === defaultValue.length && value.every(
      (item, index) => item === defaultValue[index],
    );
  }

  return value === defaultValue;
};

const parseFilterValue = (
  values: string[],
  config: TRuntimeFilterConfig,
): unknown => {
  if (values.length === 0) {
    return config.defaultValue;
  }

  let value: unknown;

  if (Array.isArray(config.defaultValue)) {
    value = [
      ...new Set(
        values
          .flatMap((item) => item.split(","))
          .map((item) => item.trim())
          .filter(Boolean),
      ),
    ];
  } else if (typeof config.defaultValue === "number") {
    value = Number(values[0]);
  } else if (typeof config.defaultValue === "boolean") {
    if (values[0] === "true") {
      value = true;
    } else if (values[0] === "false") {
      value = false;
    } else {
      value = config.defaultValue;
    }
  } else {
    value = values[0];
  }
  const result = config.schema.safeParse(value);

  return result.success ? result.data : config.defaultValue;
};

const serializeFilterValue = (value: unknown): string[] => Array.isArray(value)
  ? value.map(String)
  : [ String(value) ];

const updateSearchParam = (
  searchParams: URLSearchParams,
  filter: string,
  value: unknown,
  config: TRuntimeFilterConfig,
): void => {
  searchParams.delete(filter);

  const serializedValues = isDefaultValue(value, config.defaultValue)
    ? []
    : serializeFilterValue(value);

  for (const serializedValue of serializedValues) {
    searchParams.append(filter, serializedValue);
  }
};

export const parseFilters = (
  searchParams: TFilterSearchParams,
): TFilters => {
  const sheetKey = parseFilterValue(
    getSearchParamValues(searchParams, SHEET_FILTER_KEY),
    SHEET_FILTER_CONFIG,
  ) as string;
  const drawerKey = parseFilterValue(
    getSearchParamValues(searchParams, DRAWER_FILTER_KEY),
    DRAWER_FILTER_CONFIG,
  ) as string;
  const modalKey = parseFilterValue(
    getSearchParamValues(searchParams, MODAL_FILTER_KEY),
    MODAL_FILTER_CONFIG,
  ) as string;

  return {
    ...Object.fromEntries(
      (Object.keys(FILTERS_CONFIG) as TConfiguredFilterGroup[]).map(
        (filterGroup) => [
          filterGroup,
          Object.fromEntries(
            getFilterConfigEntries(filterGroup).map(([ filter, config ]) => [
              filter,
              parseFilterValue(getSearchParamValues(searchParams, filter), config),
            ]),
          ),
        ],
      ),
    ),
    sheets: sheetKey
      ? {
        [sheetKey]: {
          data: Object.fromEntries(
            [
              ...new Set(
                getSearchParamKeys(searchParams).filter(isSheetDataFilterKey),
              ),
            ].flatMap((filter) => {
              const value = parseFilterValue(
                getSearchParamValues(searchParams, filter),
                SHEET_FILTER_CONFIG,
              );

              return value === SHEET_FILTER_CONFIG.defaultValue
                ? []
                : [ [
                  getSheetDataKey(filter as TSheetDataFilterKey),
                  value,
                ] ];
            }),
          ),
        },
      }
      : {},
    drawers: drawerKey
      ? {
        [drawerKey]: {
          data: Object.fromEntries(
            [
              ...new Set(
                getSearchParamKeys(searchParams).filter(isDrawerDataFilterKey),
              ),
            ].flatMap((filter) => {
              const value = parseFilterValue(
                getSearchParamValues(searchParams, filter),
                DRAWER_FILTER_CONFIG,
              );

              return value === DRAWER_FILTER_CONFIG.defaultValue
                ? []
                : [ [
                  getDrawerDataKey(filter as TDrawerDataFilterKey),
                  value,
                ] ];
            }),
          ),
        },
      }
      : {},
    modals: modalKey
      ? {
        [modalKey]: {
          data: Object.fromEntries(
            [
              ...new Set(
                getSearchParamKeys(searchParams).filter(isModalDataFilterKey),
              ),
            ].flatMap((filter) => {
              const value = parseFilterValue(
                getSearchParamValues(searchParams, filter),
                MODAL_FILTER_CONFIG,
              );

              return value === MODAL_FILTER_CONFIG.defaultValue
                ? []
                : [ [
                  getModalDataKey(filter as TModalDataFilterKey),
                  value,
                ] ];
            }),
          ),
        },
      }
      : {},
    tabs: Object.fromEntries(
      [ ...new Set(getSearchParamKeys(searchParams).filter(isTabFilterKey)) ]
        .flatMap((filter) => {
          const value = parseFilterValue(
            getSearchParamValues(searchParams, filter),
            TAB_FILTER_CONFIG,
          );

          return value === TAB_FILTER_CONFIG.defaultValue
            ? []
            : [ [ filter, value ] ];
        }),
    ) as TTabFilters,
  } as TFilters;
};

export const updateSearchParams = (
  searchParams: URLSearchParams,
  filters: TFilters,
): void => {
  for (const filterGroup of Object.keys(FILTERS_CONFIG) as TConfiguredFilterGroup[]) {
    for (const [ filter, config ] of getFilterConfigEntries(filterGroup)) {
      const value = filters[filterGroup][filter as keyof TFilters[typeof filterGroup]];

      updateSearchParam(searchParams, filter, value, config);
    }
  }

  for (const filter of [ ...searchParams.keys() ].filter(isTabFilterKey)) {
    searchParams.delete(filter);
  }

  for (const [ filter, value ] of Object.entries(filters.tabs)) {
    updateSearchParam(searchParams, filter, value, TAB_FILTER_CONFIG);
  }

  searchParams.delete(SHEET_FILTER_KEY);
  for (const filter of [ ...searchParams.keys() ].filter(isSheetDataFilterKey)) {
    searchParams.delete(filter);
  }

  const [ activeSheet ] = Object.entries(filters.sheets);

  if (activeSheet) {
    const [ sheetKey, { data } ] = activeSheet;

    updateSearchParam(
      searchParams,
      SHEET_FILTER_KEY,
      sheetKey,
      SHEET_FILTER_CONFIG,
    );

    for (const [ dataKey, value ] of Object.entries(data)) {
      updateSearchParam(
        searchParams,
        getSheetDataFilterKey(dataKey),
        value,
        SHEET_FILTER_CONFIG,
      );
    }
  }

  searchParams.delete(DRAWER_FILTER_KEY);
  for (const filter of [ ...searchParams.keys() ].filter(isDrawerDataFilterKey)) {
    searchParams.delete(filter);
  }

  const [ activeDrawer ] = Object.entries(filters.drawers);

  if (activeDrawer) {
    const [ drawerKey, { data } ] = activeDrawer;

    updateSearchParam(
      searchParams,
      DRAWER_FILTER_KEY,
      drawerKey,
      DRAWER_FILTER_CONFIG,
    );

    for (const [ dataKey, value ] of Object.entries(data)) {
      updateSearchParam(
        searchParams,
        getDrawerDataFilterKey(dataKey),
        value,
        DRAWER_FILTER_CONFIG,
      );
    }
  }

  searchParams.delete(MODAL_FILTER_KEY);
  for (const filter of [ ...searchParams.keys() ].filter(isModalDataFilterKey)) {
    searchParams.delete(filter);
  }

  const [ activeModal ] = Object.entries(filters.modals);

  if (activeModal) {
    const [ modalKey, { data } ] = activeModal;

    updateSearchParam(
      searchParams,
      MODAL_FILTER_KEY,
      modalKey,
      MODAL_FILTER_CONFIG,
    );

    for (const [ dataKey, value ] of Object.entries(data)) {
      updateSearchParam(
        searchParams,
        getModalDataFilterKey(dataKey),
        value,
        MODAL_FILTER_CONFIG,
      );
    }
  }
};

export const getApiFilterKey = <TFilter extends TStoreFilterKey>(
  filter: TFilter,
): TApiFilterKey<TFilter> => {
  const KEY_SLICE_INDEX = 1;
  return filter.slice(
    filter.indexOf(":") + KEY_SLICE_INDEX,
  ) as TApiFilterKey<TFilter>;
};

export const getApiFilters = <TFilterGroup extends TConfiguredFilterGroup>(
  searchParams: TFilterSearchParams,
  filterGroup: TFilterGroup,
): TApiFilters<TFilterGroup> => Object.fromEntries(
  Object.entries(parseFilters(searchParams)[filterGroup]).map(
    ([ filter, value ]) => [
      getApiFilterKey(filter as TStoreFilterKey),
      value,
    ],
  ),
) as TApiFilters<TFilterGroup>;
